import React from 'react';
import { SocialNetwork } from './types';

const iconProps = {
  width: '100%',
  height: '100%',
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.8,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

const filledProps = {
  width: '100%',
  height: '100%',
  viewBox: '0 0 24 24',
  fill: 'currentColor',
};

// Paths for instagram/whatsapp mirror the prototype's own SVGs exactly (prototipo_carga_header_v18.html).
// Facebook/YouTube/LinkedIn/TikTok have no real SVG there either — the prototype just drops the
// bare glyph ('f', '▶', 'in', '♪') as text inside the circular badge, so we do the same here.
const SOCIAL_ICON_PATHS: Partial<Record<SocialNetwork, string>> = {
  instagram:
    'M7.2 2h9.6A5.2 5.2 0 0 1 22 7.2v9.6a5.2 5.2 0 0 1-5.2 5.2H7.2A5.2 5.2 0 0 1 2 16.8V7.2A5.2 5.2 0 0 1 7.2 2Zm0 1.8a3.4 3.4 0 0 0-3.4 3.4v9.6a3.4 3.4 0 0 0 3.4 3.4h9.6a3.4 3.4 0 0 0 3.4-3.4V7.2a3.4 3.4 0 0 0-3.4-3.4H7.2Zm10.15 1.35a1.25 1.25 0 1 1 0 2.5 1.25 1.25 0 0 1 0-2.5ZM12 7.05A4.95 4.95 0 1 1 12 16.95 4.95 4.95 0 0 1 12 7.05Zm0 1.8A3.15 3.15 0 1 0 12 15.15 3.15 3.15 0 0 0 12 8.85Z',
  twitter:
    'M22 5.9c-.7.3-1.5.6-2.3.7.8-.5 1.5-1.3 1.8-2.2-.8.5-1.7.8-2.6 1A4.1 4.1 0 0 0 11.8 9c0 .3 0 .6.1.9-3.4-.2-6.5-1.8-8.5-4.3-.4.6-.6 1.4-.6 2.2 0 1.5.7 2.7 1.8 3.5-.7 0-1.3-.2-1.9-.5v.1c0 2 1.4 3.7 3.3 4.1-.4.1-.7.2-1.1.2-.3 0-.5 0-.8-.1.5 1.6 2 2.8 3.8 2.8A8.2 8.2 0 0 1 2 19.5 11.6 11.6 0 0 0 8.3 21c7.5 0 11.6-6.2 11.6-11.6v-.5c.8-.6 1.5-1.3 2.1-2.1z',
  x: 'M4 4l7.5 8.5L4.4 20H7l5.2-5.9L16.5 20H20l-7.9-9L19.6 4H17l-4.8 5.5L8.5 4H4Z',
};

const SOCIAL_GLYPHS: Partial<Record<SocialNetwork, string>> = {
  facebook: 'f',
  youtube: '▶',
  tiktok: '♪',
  linkedin: 'in',
};

const DEFAULT_SOCIAL_ICON_PATH =
  'M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm0 4a3 3 0 1 1 0 6 3 3 0 0 1 0-6zm0 12.2c-2.3 0-4.4-1-5.9-2.6.1-2 3.9-3.1 5.9-3.1s5.8 1.1 5.9 3.1A8 8 0 0 1 12 18.2z';

function makeSocialIcon(path: string): React.FC {
  const Icon: React.FC = () => (
    <svg {...filledProps} aria-hidden='true'>
      <path d={path} />
    </svg>
  );
  return Icon;
}

function makeGlyphIcon(glyph: string): React.FC {
  const Icon: React.FC = () => <span aria-hidden='true'>{glyph}</span>;
  return Icon;
}

const SOCIAL_NETWORKS: SocialNetwork[] = [
  'facebook',
  'instagram',
  'twitter',
  'x',
  'youtube',
  'tiktok',
  'linkedin',
];

export const SOCIAL_ICONS: Record<SocialNetwork, React.FC> = Object.fromEntries(
  SOCIAL_NETWORKS.map((network) => [
    network,
    SOCIAL_GLYPHS[network]
      ? makeGlyphIcon(SOCIAL_GLYPHS[network])
      : makeSocialIcon(SOCIAL_ICON_PATHS[network]),
  ]),
) as Record<SocialNetwork, React.FC>;

export const DefaultSocialIcon: React.FC = makeSocialIcon(DEFAULT_SOCIAL_ICON_PATH);

const PhoneIcon: React.FC = () => (
  <svg {...filledProps} aria-hidden='true'>
    <path d='M6.62 10.79a15.46 15.46 0 0 0 6.59 6.59l2.2-2.2a1 1 0 0 1 1.02-.24c1.12.37 2.33.57 3.57.57a1 1 0 0 1 1 1V20a1 1 0 0 1-1 1C10.61 21 3 13.39 3 4a1 1 0 0 1 1-1h3.5a1 1 0 0 1 1 1c0 1.25.2 2.45.57 3.57a1 1 0 0 1-.25 1.02l-2.2 2.2Z' />
  </svg>
);

const EmailIcon: React.FC = () => (
  <svg {...filledProps} aria-hidden='true'>
    <path d='M20 4H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2Zm0 4-8 5-8-5V6l8 5 8-5v2Z' />
  </svg>
);

const WhatsappIcon: React.FC = () => (
  <svg {...filledProps} aria-hidden='true'>
    <path d='M12.04 2a9.83 9.83 0 0 0-8.43 14.9L2 22l5.22-1.55A9.96 9.96 0 1 0 12.04 2Zm0 17.96a8.02 8.02 0 0 1-4.09-1.12l-.29-.17-3.1.92.94-3.02-.19-.31a7.9 7.9 0 1 1 6.73 3.7Zm4.4-5.92c-.24-.12-1.43-.7-1.65-.78-.22-.08-.38-.12-.54.12-.16.24-.62.78-.76.94-.14.16-.28.18-.52.06-.24-.12-1.01-.37-1.93-1.19-.71-.64-1.2-1.42-1.34-1.66-.14-.24-.02-.37.1-.49.11-.11.24-.28.36-.42.12-.14.16-.24.24-.4.08-.16.04-.3-.02-.42-.06-.12-.54-1.3-.74-1.78-.2-.47-.39-.41-.54-.42h-.46c-.16 0-.42.06-.64.3-.22.24-.84.82-.84 2s.86 2.32.98 2.48c.12.16 1.69 2.58 4.1 3.62.57.25 1.02.4 1.37.51.58.18 1.1.16 1.51.1.46-.07 1.43-.59 1.63-1.15.2-.56.2-1.04.14-1.14-.06-.1-.22-.16-.46-.28Z' />
  </svg>
);

export const CONTACT_ICONS = {
  phone: PhoneIcon,
  email: EmailIcon,
  whatsapp: WhatsappIcon,
};

export const MenuIcon: React.FC = () => (
  <svg {...filledProps} aria-hidden='true'>
    <path d='M3 6h18v2H3V6Zm0 5h18v2H3v-2Zm0 5h18v2H3v-2Z' />
  </svg>
);

export const CloseIcon: React.FC = () => (
  <svg {...iconProps} aria-hidden='true'>
    <path d='M6 6l12 12M18 6 6 18' />
  </svg>
);
